/* eslint-disable no-confusing-arrow */
/* eslint-disable max-len */
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
    Box,
    Button,
    Grid,
    Tooltip,
    Typography
} from '@mui/material';

import {
    Archive,
    Delete,
    Edit,
    Unarchive,
    LocalShipping,
    Construction,
    CardMembership,
    Forest,
    ExpandLess,
    ExpandMore,
    Map as MapIcon
} from '@mui/icons-material';

import { ReactComponent as FairTransportEmblem } from '../../assets/icons/fairtransport-emblem.svg';

import {
    Block,
    Description,
    DriveLine,
    Fetch,
    Header,
    List,
    Numbers,
    Popup,
    SectionDivider
} from '../CommonComponents';

import { Container, Paper } from '../StyledComponents';

import OrderStatus from './OrderStatus';
import RequirementsSection from './OrderRequirements';

import { getOrder, setOrdersArchived, setOrdersDeleted } from '../../api/orders';

import type { FetchComponentPropsType } from '../CommonComponents/Fetch/Fetch';
import OrderMap from './OrderMap';
import { OrderType } from '../../types';
import ChangeOrderContact from './ChangeOrderContact';
import { formatOrderPublishedFor, getFormattedOrderExpiryDate } from '../../utils/order';
import OrderOffers from './OrderOffers';
import OrderFiles from './OrderFiles';
import OfferChat from '../Offers/OfferChat';
import toastMessage from '../../utils/toast';
import OrderStatusAction from './OrderStatusAction';
import { getTenantLinkForOrders } from '../../utils/tenant-links';

const OrderDetailsView: React.FC<FetchComponentPropsType<OrderType>> = (props) => {
    const { entity, refetch } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const openOfferId = parseInt(searchParams.get('ooid') ?? '', 10);

    const [showEditWarning, setShowEditWarning] = React.useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);
    const [showArchiveWarning, setShowArchiveWarning] = React.useState(false);
    const [showMap, setShowMap] = React.useState(false);

    const showDeleteButton = React.useMemo(() => (
        ['DRAFTED', 'PUBLISHED', 'COMPLETED'].some((s) => s === entity.status)
    ), [entity]);

    const showArchiveButton = React.useMemo(() => (
        ['DRAFTED', 'PUBLISHED', 'COMPLETED'].some((s) => s === entity.status)
    ), [entity]);

    const showEditButton = React.useMemo(() => (
        ['DRAFTED', 'PUBLISHED'].some((s) => s === entity.status)
    ), [entity]);

    const showChangeStatusButton = React.useMemo(() => (
        ['DRAFTED', 'PUBLISHED', 'ACCEPTED', 'STARTED'].some((s) => s === entity.status)
    ), [entity]);

    const onEdit = React.useCallback(() => {
        setShowEditWarning(false);
        if (entity.id) {
            navigate(`/orders/edit/${entity.id}`);
        }
    }, [entity.id, navigate]);

    const onDelete = React.useCallback(async () => {
        try {
            setShowDeleteWarning(false);
            const result = (await setOrdersDeleted({ ids: [entity.id] })).at(0);
            if (result.ok) {
                toastMessage('Uppdraget borttaget');
                navigate('/orders', { replace: true });
            } else {
                toastMessage(result.error?.message || 'Uppdraget kunde inte tas bort.');
            }
        } catch (e: any) {
            toastMessage(e, 'Uppdraget kunde inte tas bort.');
        }
    }, [entity.id, navigate]);

    const onArchive = React.useCallback(async () => {
        try {
            setShowArchiveWarning(false);
            const result = (await setOrdersArchived({ ids: [entity.id], archive: !entity.isArchived })).at(0);
            refetch();
            if (result.ok) {
                toastMessage(`Uppdraget ${entity.isArchived ? 'av' : ''}arkiverat`);
            } else {
                toastMessage(
                    result.error?.message,
                    `Uppdraget kunde inte ${entity.isArchived ? 'avarkiveras' : 'arkiveras'}.`
                );
            }
        } catch (e: any) {
            toastMessage(e, `Uppdraget kunde inte ${entity.isArchived ? 'avarkiveras' : 'arkiveras'}.`);
        }
    }, [entity.id, entity.isArchived, refetch]);

    const onEditClick = React.useCallback(() => {
        if (entity.status === 'PUBLISHED' && !entity.isArchived) {
            setShowEditWarning(true);
        } else {
            onEdit();
        }
    }, [onEdit, entity.isArchived, entity.status]);

    const onDeleteClick = React.useCallback(() => {
        setShowDeleteWarning(true);
    }, []);

    const onArchiveClick = React.useCallback(() => {
        setShowArchiveWarning(true);
    }, []);

    const entityMetadataLength = React.useMemo(() => (
        entity ? [
            entity.vehicleTypes,
            entity.equipment,
            entity.sustainability,
            entity.licencesCertificates,
            entity.customLicencesCertificates
        ].reduce((sum, arr) => sum + (arr ? arr.length : 0), 0) : 0
    ), [entity]);

    const [metaDataCollapsed, setMetaDataCollapsed] = React.useState(false);

    const sustainabilityWithoutFairtransport = React.useMemo(
        () => (entity?.sustainability || []).filter((sust) => sust !== 'Fair Transport'),
        [entity?.sustainability]
    );

    const mustBeFairtransport = React.useMemo(
        () => (entity?.sustainability || []).includes('Fair Transport'),
        [entity?.sustainability]
    );

    return !entity ? null : (
        <Container>
            <Popup
                open={showEditWarning}
                title="Vill du verkligen ändra ditt publicerade uppdrag?"
                body="Notiser om uppdaterat uppdrag kommer att skickas ut till alla som har matchande bevakningar."
                okLabel="Ja"
                cancelLabel="Nej"
                handleOk={onEdit}
                handleClose={() => setShowEditWarning(false)}
            />

            <Popup
                open={showDeleteWarning}
                title="Vill du verkligen ta bort uppdraget?"
                body="Du kommer inte kunna återställa det igen när du tagit bort det."
                okLabel="Ja"
                cancelLabel="Nej"
                handleOk={onDelete}
                handleClose={() => setShowDeleteWarning(false)}
            />

            <Popup
                open={showArchiveWarning}
                title="Vill du verkligen arkivera uppdraget?"
                body='Du hittar alla arkiverade uppdrag under fliken "Arkiverade", där du enkelt kan avarkivera dem igen.'
                okLabel="Ja"
                cancelLabel="Nej"
                handleOk={onArchive}
                handleClose={() => setShowArchiveWarning(false)}
            />

            <Header
                title="Boka transport"
                // eslint-disable-next-line no-nested-ternary
                CustomComponent={(
                    <>
                        <Button
                            sx={{ ml: 1 }}
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={() => setShowMap((visible) => !visible)}
                        >
                            {showMap ? 'Dölj karta' : 'Visa karta'}
                        </Button>
                        {entity.isArchived ? (
                            <Button sx={{ ml: 1 }} variant="outlined" startIcon={<Unarchive />} onClick={onArchive}>
                                Avarkivera
                            </Button>
                        ) : (
                            <Tooltip
                                title="Du kan inte ändra uppdrag som har aktiva intresseanmälningar."
                                placement="top"
                                arrow
                                disableInteractive
                                disableHoverListener={!entity.hasPendingOffers}
                            >
                                <span>
                                    {showDeleteButton && (
                                        <Button
                                            sx={{ ml: 1 }}
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={onDeleteClick}
                                            disabled={entity.hasPendingOffers}
                                        >
                                            Ta bort
                                        </Button>
                                    )}
                                    {showArchiveButton && (
                                        <Button
                                            sx={{ ml: 1 }}
                                            variant="outlined"
                                            startIcon={<Archive />}
                                            onClick={onArchiveClick}
                                            disabled={entity.hasPendingOffers}
                                        >
                                            Arkivera
                                        </Button>
                                    )}
                                    {showEditButton && (
                                        <Button
                                            sx={{ ml: 1 }}
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={onEditClick}
                                            disabled={entity.hasPendingOffers}
                                        >
                                            Redigera
                                        </Button>
                                    )}
                                    {showChangeStatusButton && (
                                        <OrderStatusAction data={entity} refetch={refetch} />
                                    )}
                                </span>
                            </Tooltip>
                        )}
                    </>
                )}
            />

            <Grid container spacing={2}>
                {showMap && (
                    <Grid item xs={12}>
                        <Block>
                            <OrderMap
                                entityType="LL"
                                pickupLat={entity.pickupLat}
                                pickupLng={entity.pickupLng}
                                deliveryLat={entity.deliveryLat}
                                deliveryLng={entity.deliveryLng}
                            />
                        </Block>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <OrderStatus data={entity} />
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Paper>
                        <DriveLine small data={entity} />
                        <Numbers
                            small
                            data={[
                                { title: 'Totalvikt [kg]', value: entity.grossWeight },
                                { title: 'FDR-vikt [kg]', value: entity.chargeableWeight },
                                { title: 'Volym [m³]', value: entity.volume },
                                { title: 'Flakmeter', value: entity.loadingMeters },
                                { title: 'Pallplatser', value: entity.palletPlaces },
                                { title: 'Pallar', value: entity.pallets },
                                // { title: 'Stycken', value: entity.pieces }
                            ]}
                        />
                        {entityMetadataLength === 0 && (
                            <Grid container spacing={2} style={{ marginTop: 10 }}>
                                <Grid item xs={12} lg={6}>
                                    <Description
                                        title="Publik beskrivning"
                                        text={entity.publicDescription || 'Ingen publik beskrivning tillagd.'}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Description
                                        title="Privat beskrivning"
                                        text={entity.privateDescription || 'Ingen privat beskrivning tillagd.'}
                                    />
                                </Grid>
                            </Grid>
                        )}
                        {entityMetadataLength > 0 && (
                            <>
                                <SectionDivider
                                    label={(
                                        <>
                                            {`krav (${entityMetadataLength})`}
                                            {metaDataCollapsed ? <ExpandMore /> : <ExpandLess />}
                                        </>
                                    )}
                                    marginTop={1}
                                    marginBottom={1}
                                    onClick={() => setMetaDataCollapsed(!metaDataCollapsed)}
                                />
                                {!metaDataCollapsed && (
                                    <>
                                        {mustBeFairtransport && (
                                            <Grid item xs={12} p={2} alignItems="center">
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    columnGap: 1
                                                }}
                                                >
                                                    <FairTransportEmblem style={{ height: 25, width: 25 }} color="inherit" />
                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 300 }}>
                                                        Kräver certifiering av Fair Transport
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Grid
                                                container
                                                rowSpacing={1}
                                                columnSpacing={0}
                                                alignItems="flex-start"
                                            >
                                                {(entity.vehicleTypes || []).length > 0 && (
                                                    <RequirementsSection
                                                        label="Fordonstyper"
                                                        entityMetadata={entity.vehicleTypes || []}
                                                        icon={<LocalShipping fontSize="small" />}
                                                        isMyJob
                                                    />
                                                )}
                                                {(entity.equipment || []).length > 0 && (
                                                    <RequirementsSection
                                                        label="Utrustning"
                                                        entityMetadata={entity.equipment || []}
                                                        icon={<Construction fontSize="small" />}
                                                        isMyJob
                                                    />
                                                )}
                                                {sustainabilityWithoutFairtransport.length > 0 && (
                                                    <RequirementsSection
                                                        label="Hållbarhet"
                                                        entityMetadata={entity.sustainability}
                                                        icon={<Forest fontSize="small" />}
                                                        isMyJob
                                                    />
                                                )}
                                                {[
                                                    ...(entity.licencesCertificates || []),
                                                    ...(entity.customLicencesCertificates || [])
                                                ].length > 0 && (
                                                    <RequirementsSection
                                                        label="Licenser & certifikat"
                                                        entityMetadata={[
                                                            ...(entity.licencesCertificates || []),
                                                            ...(entity.customLicencesCertificates || [])
                                                        ]}
                                                        icon={<CardMembership fontSize="small" />}
                                                        isMyJob
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Block
                        title="Detaljer"
                    >
                        <List
                            data={[
                                {
                                    name: 'Publiceras för',
                                    value: formatOrderPublishedFor(entity)
                                },
                                {
                                    name: 'Visa ert företagsnamn',
                                    value: entity.showTenantName,
                                    type: 'y/n'
                                },
                                {
                                    name: 'Riktpris',
                                    value: entity.listPrice,
                                    type: 'currency'
                                },
                                // {
                                //     name: 'Avgift',
                                //     value: entity.fee,
                                //     type: 'currency'
                                // },
                                {
                                    name: 'Intresseanmälan senast',
                                    value: getFormattedOrderExpiryDate(entity),
                                    type: 'datetime'
                                },
                                // {
                                //     name: 'Antal visningar',
                                //     value: entity.viewCount,
                                //     type: 'number'
                                // },
                            ]}
                        />
                        {entityMetadataLength > 0 && (
                            <Grid container spacing={2} justifyContent="center" style={{ marginBottom: 10 }}>
                                <Grid item xs={12} mx={2}>
                                    <Description
                                        title="Publik beskrivning"
                                        text={entity.publicDescription || 'Ingen publik beskrivning tillagd.'}
                                    />
                                </Grid>
                                <Grid item xs={12} mx={2} mb={2}>
                                    <Description
                                        title="Privat beskrivning"
                                        text={entity.privateDescription || 'Ingen privat beskrivning tillagd.'}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Block>
                </Grid>

                {['ACCEPTED', 'STARTED', 'COMPLETED'].some((s) => s === entity.status) && (
                    <>
                        <Grid item xs={12} lg={6}>
                            <Block
                                title="Kontaktuppgifter"
                                ActionComponent={entity && !entity.isArchived ? (
                                    <ChangeOrderContact
                                        orderId={entity.id}
                                        contactId={entity.tenantContactId}
                                        contactName={entity.tenantContactName}
                                        contactType="shipper-contact"
                                        refetch={refetch}
                                    />
                                ) : undefined}
                            >
                                <List
                                    data={[
                                        {
                                            name: 'Företag',
                                            value: getTenantLinkForOrders(entity),
                                        },
                                        {
                                            name: 'Företag, e-post',
                                            value: entity.acceptedTenantEmail,
                                            type: 'email'
                                        },
                                        {
                                            name: 'Företag, telefon',
                                            value: entity.acceptedTenantPhone,
                                            type: 'phone'
                                        },
                                        {
                                            name: 'Kontaktperson',
                                            value: entity.acceptedTenantContactName
                                        },
                                        {
                                            name: 'Kontaktperson, e-post',
                                            value: entity.acceptedTenantContactEmail,
                                            type: 'email'
                                        },
                                        {
                                            name: 'Kontaktperson, mobil',
                                            value: entity.acceptedTenantContactPhone,
                                            type: 'phone'
                                        },
                                        {
                                            name: 'Kontaktperson, telefon',
                                            value: entity.acceptedTenantContactPhoneAlt,
                                            type: 'phone'
                                        }
                                    ]}
                                />
                            </Block>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <Block title="Meddelanden" contentPadding>
                                <OfferChat
                                    id={entity.acceptedOfferId}
                                    orderId={entity.id}
                                    recipient={entity.acceptedTenantName}
                                    canWrite={!entity.isArchived} // Todo: use entity.isOpen here when implemented for both offer and order.
                                    maxHeight="290px"
                                />
                            </Block>
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    <OrderOffers
                        order={entity}
                        refetch={refetch}
                        openOfferId={openOfferId}
                    />
                </Grid>
                <Grid item xs={12}>
                    <OrderFiles orderId={entity.id} />
                </Grid>
            </Grid>
        </Container>
    );
};

export default function OrderDetails() {
    const { id } = useParams() as unknown as { id: number };
    const fetchOrder = React.useCallback(() => getOrder(id), [id]);

    return (
        <Fetch<OrderType>
            fetch={fetchOrder}
            Component={OrderDetailsView}
            name="uppdraget"
        />
    );
}
